import React, { useState, useEffect, useCallback } from 'react';
import { Search, FileText, Download, Eye, Loader2, CheckCircle, XCircle, Shield } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Store token in localStorage
const KIOSK_TOKEN_KEY = 'gecode_kiosk_token';
const KIOSK_INFO_KEY = 'gecode_kiosk_info';

export default function Kiosk() {
  const [isActivated, setIsActivated] = useState(false);
  const [kioskInfo, setKioskInfo] = useState(null);
  const [token, setToken] = useState('');
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState('');
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [drawings, setDrawings] = useState([]);
  
  // View state
  const [viewingPdf, setViewingPdf] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  // Check for existing activation on mount
  useEffect(() => {
    const savedToken = localStorage.getItem(KIOSK_TOKEN_KEY);
    const savedInfo = localStorage.getItem(KIOSK_INFO_KEY);
    
    if (savedToken && savedInfo) {
      setToken(savedToken);
      setKioskInfo(JSON.parse(savedInfo));
      setIsActivated(true);
    }
  }, []);

  const handleActivate = async () => {
    if (!token.trim()) {
      setError('Digite o token de ativação');
      return;
    }
    
    setActivating(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/kiosk/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: token.trim() })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Falha na ativação');
      }
      
      // Save to localStorage
      localStorage.setItem(KIOSK_TOKEN_KEY, token.trim());
      localStorage.setItem(KIOSK_INFO_KEY, JSON.stringify(data.kiosk));
      
      setKioskInfo(data.kiosk);
      setIsActivated(true);
      toast.success(`Kiosk "${data.kiosk.name}" ativado com sucesso!`);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setActivating(false);
    }
  };

  const handleDeactivate = () => {
    localStorage.removeItem(KIOSK_TOKEN_KEY);
    localStorage.removeItem(KIOSK_INFO_KEY);
    setIsActivated(false);
    setKioskInfo(null);
    setToken('');
    setDrawings([]);
    setViewingPdf(null);
    toast.info('Kiosk desativado');
  };

  const handleSearch = useCallback(async () => {
    if (!searchTerm || searchTerm.length < 3) {
      toast.error('Digite pelo menos 3 caracteres para buscar');
      return;
    }
    
    setSearching(true);
    
    try {
      const savedToken = localStorage.getItem(KIOSK_TOKEN_KEY);
      const response = await fetch(
        `${API_URL}/api/kiosk/drawings/search?search=${encodeURIComponent(searchTerm)}&token=${encodeURIComponent(savedToken)}`
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erro na busca');
      }
      
      setDrawings(data.drawings);
      
      if (data.drawings.length === 0) {
        toast.info('Nenhum desenho encontrado');
      }
    } catch (err) {
      toast.error(err.message);
      if (err.message.includes('IP') || err.message.includes('token')) {
        handleDeactivate();
      }
    } finally {
      setSearching(false);
    }
  }, [searchTerm]);

  const handleView = async (drawing) => {
    setLoadingPdf(true);
    
    try {
      const savedToken = localStorage.getItem(KIOSK_TOKEN_KEY);
      const url = `${API_URL}/api/kiosk/drawings/view/${drawing.id}?token=${encodeURIComponent(savedToken)}`;
      
      // Open in new tab or set for iframe
      setViewingPdf({
        url,
        code: drawing.code || drawing.base_code,
        drawing
      });
    } catch (err) {
      toast.error('Erro ao carregar PDF');
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleDownload = async (drawing) => {
    try {
      const savedToken = localStorage.getItem(KIOSK_TOKEN_KEY);
      const url = `${API_URL}/api/kiosk/drawings/download/${drawing.id}?token=${encodeURIComponent(savedToken)}`;
      
      // Open download in new tab
      window.open(url, '_blank');
      toast.success('Download iniciado');
    } catch (err) {
      toast.error('Erro ao baixar arquivo');
    }
  };

  // Activation Screen
  if (!isActivated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-800/50 border-slate-700 backdrop-blur">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl text-white">GECODE Kiosk</CardTitle>
            <CardDescription className="text-slate-400">
              Digite o token de ativação fornecido pelo administrador
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Input
                type="text"
                placeholder="Token de ativação (kiosk_...)"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleActivate()}
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                data-testid="kiosk-token-input"
              />
            </div>
            
            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 p-3 rounded-lg">
                <XCircle className="w-4 h-4" />
                {error}
              </div>
            )}
            
            <Button
              onClick={handleActivate}
              disabled={activating || !token.trim()}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="kiosk-activate-btn"
            >
              {activating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Ativando...
                </>
              ) : (
                'Ativar Kiosk'
              )}
            </Button>
            
            <p className="text-xs text-slate-500 text-center">
              Terminal de consulta de desenhos
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main Kiosk Interface
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">GECODE</h1>
              <p className="text-sm text-slate-400">{kioskInfo?.name}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="bg-green-900/30 text-green-400 border-green-700">
              <CheckCircle className="w-3 h-3 mr-1" />
              Ativo
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDeactivate}
              className="text-slate-400 hover:text-white"
            >
              Desativar
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Search Bar */}
        <Card className="bg-slate-800/50 border-slate-700 mb-6">
          <CardContent className="p-6">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type="text"
                  placeholder="Buscar código do desenho (ex: 8-099-66108)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 text-lg h-12"
                  data-testid="kiosk-search-input"
                />
              </div>
              <Button
                onClick={handleSearch}
                disabled={searching || searchTerm.length < 3}
                className="bg-blue-600 hover:bg-blue-700 h-12 px-8"
                data-testid="kiosk-search-btn"
              >
                {searching ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Search className="w-5 h-5 mr-2" />
                    Buscar
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Results */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Resultados</CardTitle>
              <CardDescription className="text-slate-400">
                {drawings.length > 0 ? `${drawings.length} desenho(s) encontrado(s)` : 'Busque por um código'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {drawings.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Digite um código e clique em Buscar</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {drawings.map((drawing) => (
                    <div
                      key={drawing.id}
                      className={`p-4 rounded-lg border transition-colors cursor-pointer ${
                        viewingPdf?.drawing?.id === drawing.id
                          ? 'bg-blue-900/30 border-blue-600'
                          : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
                      }`}
                      onClick={() => handleView(drawing)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <code className="text-lg font-mono text-white">
                            {drawing.code || drawing.base_code}
                          </code>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className={
                              drawing.file_type === 'PDF' 
                                ? 'bg-red-900/30 text-red-400 border-red-700' 
                                : 'bg-blue-900/30 text-blue-400 border-blue-700'
                            }>
                              {drawing.file_type}
                            </Badge>
                            <span className="text-sm text-slate-400">
                              Rev. {drawing.latest_revision || drawing.revision || '00'}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleView(drawing);
                            }}
                            className="text-slate-400 hover:text-white"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(drawing);
                            }}
                            className="text-slate-400 hover:text-white"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* PDF Viewer */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Visualização</CardTitle>
              <CardDescription className="text-slate-400">
                {viewingPdf ? viewingPdf.code : 'Selecione um desenho para visualizar'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingPdf ? (
                <div className="flex items-center justify-center h-[500px]">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : viewingPdf ? (
                <div className="space-y-4">
                  <iframe
                    src={viewingPdf.url}
                    className="w-full h-[500px] rounded-lg border border-slate-600 bg-white"
                    title={`PDF: ${viewingPdf.code}`}
                  />
                  <Button
                    onClick={() => handleDownload(viewingPdf.drawing)}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Baixar {viewingPdf.code}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[500px] text-slate-500">
                  <div className="text-center">
                    <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Clique em um desenho para visualizar</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
